import { useMemo } from 'react';
import dayjs from 'dayjs';
import { InvoiceModel } from '@frontend/api-invoices';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import { ChipProps, formatDate } from '@frontend/design-system';
import { InvoiceCardDetails } from './invoice-card/invoice-card-details';

export type InvoiceCardProps = React.PropsWithChildren<{
  invoice: InvoiceModel;
  className?: string;
}>;

export function InvoiceCard({ invoice, className, children }: InvoiceCardProps) {
  const { t } = useTranslation();

  const formattedBilledAt = useMemo(() => {
    return formatDate(invoice.billedAt, 'MMM D, YYYY', true);
  }, [invoice.billedAt]);

  const formattedBilledAmount = useMemo(() => {
    return formatCentsToCurrency(invoice.billedAmount, false);
  }, [invoice]);

  const formattedOwedAmount = useMemo(() => {
    return formatCentsToCurrency(invoice.billedAmount - (invoice.payment?.paidAmount || 0), false);
  }, [invoice]);

  const billedAtData = useMemo(() => {
    try {
      const daysPassed = dayjs().startOf('day').diff(dayjs(invoice.billedAt).startOf('day'), 'day');

      let variant: ChipProps['variant'] = 'primary';
      let daysPassedLabel = t('Due today');

      if (daysPassed >= 1 && daysPassed < 30) {
        variant = 'warn';
        daysPassedLabel = daysPassed === 1 ? t('1 day overdue') : t('{{ count }} days overdue', { count: daysPassed });
      } else if (daysPassed >= 30) {
        variant = 'critical';
        daysPassedLabel = daysPassed === 30 ? t('30 days overdue') : t('30+ days overdue');
      }

      return {
        variant,
        daysPassedLabel,
      };
    } catch (error) {
      paymentsSentry.error(
        'Failed to calculate days passed',
        'InvoiceCard',
        error instanceof Error ? error.message : 'Unknown error calculating days passed'
      );
      return {
        variant: 'neutral' as ChipProps['variant'],
        daysPassedLabel: '',
      };
    }
  }, [invoice.billedAt, t]);

  return (
    <InvoiceCardDetails
      className={className}
      invoice={invoice}
      formattedBilledAt={formattedBilledAt}
      billedAtData={billedAtData}
      formattedBilledAmount={formattedBilledAmount}
      formattedOwedAmount={formattedOwedAmount}
    >
      {children}
    </InvoiceCardDetails>
  );
}
